import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, LogOut, User, Shield, Plus, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import './Header.css';

interface HeaderProps {
    // Azure/Storage status
    azureConnected?: boolean;
    azureStatus?: string;
    isRefreshing?: boolean;
    onRefreshFromAzure?: () => void;
    // Event management
    onAddEvent?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
    azureConnected = false,
    azureStatus = 'checking',
    isRefreshing = false,
    onRefreshFromAzure, 
    onAddEvent
}) => {
    const { user, isAuthenticated, isCalendarEditor, login, logout, loading, isBypassMode } = useAuth();

    if (loading) {
        return (
            <header className="header">
                <div className="header-content">
                    <div className="header-title">
                        <img src="/thesiscalendar.png" alt="Thesis Calendar Logo" className="header-logo" />
                        <div className="header-text">
                            <h1>Thesis SM Calendar</h1>
                        </div>
                    </div>
                    <div className="header-auth">
                        <div className="loading-spinner">Loading...</div>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className="header">
            <div className="header-content">
                <div className="header-title">
                    <img src="/thesiscalendar.png" alt="Thesis Calendar Logo" className="header-logo" />
                    <div className="header-text">
                        <h1>Thesis SM Calendar</h1>
                        <p>Stay informed about important Thesis SM events</p>
                    </div>
                </div>

                {/* Calendar Actions - only show when authenticated */}
                {isAuthenticated && (
                    <div className="header-calendar-actions">
                        {/* Azure Storage Status */}
                        <div className="storage-status">
                            {azureConnected ? (
                                <>
                                    <Cloud size={16} className="storage-icon connected" />
                                    <span className="storage-text">
                                        {azureStatus === 'loading' ? 'Syncing...' :
                                            azureStatus === 'error' ? 'Azure Error' :
                                                'Azure Connected'}
                                    </span>
                                    {onRefreshFromAzure && (
                                        <button
                                            className={`btn btn-sm btn-secondary ${isRefreshing ? 'refreshing' : ''}`}
                                            onClick={onRefreshFromAzure}
                                            disabled={isRefreshing || azureStatus === 'loading'}
                                            title="Refresh from Azure Blob Storage"
                                        >
                                            <RefreshCw size={14} className={isRefreshing || azureStatus === 'loading' ? 'spinning' : ''} />
                                        </button>
                                    )}                                    
                                </>
                            ) : (
                                <>
                                    <CloudOff size={16} className="storage-icon disconnected" />
                                    <span className="storage-text">Not connected</span>
                                </>
                            )}
                        </div>

                        {/* Add Event Button - only for editors */}
                        {isCalendarEditor && onAddEvent && (
                            <button
                                className="btn btn-primary add-event-btn"
                                onClick={onAddEvent}
                                disabled={!azureConnected}
                            >
                                <Plus size={20} />
                                Add Event
                            </button>
                        )}
                    </div>
                )}

                <div className="header-auth">
                    {isAuthenticated ? (
                        <div className="user-info">
                            <div className="user-details">
                                <div className="user-avatar">
                                    <User size={20} />
                                </div>
                                <div className="user-text">
                                    <span className="user-name">{user?.name || user?.username}</span>
                                    <div className="user-roles">
                                        {isBypassMode && (
                                            <span className="role-badge" style={{ backgroundColor: '#ffa500' }}>
                                                Demo Mode
                                            </span>
                                        )}
                                        {isCalendarEditor && (
                                            <span className="role-badge">
                                                <Shield size={12} />
                                                Calendar Editor
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!isBypassMode && (
                                <button className="btn btn-secondary logout-btn" onClick={logout}>
                                    <LogOut size={16} />
                                    Sign Out
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="auth-buttons">
                            <button className="btn btn-primary" onClick={login}>
                                <LogIn size={16} />
                                Sign In with Microsoft
                            </button>
                        </div>
                    )}
                </div>
            </div>            
        </header>
    );
};